import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch {
        return setState("error");
      }
      if (tries++ < 8) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-sand-deep flex items-center justify-center px-5" data-testid="payment-success">
      <div className="bg-white rounded-[2rem] soft-shadow-lg p-10 max-w-md w-full text-center">
        {state === "checking" && <><Loader2 className="h-12 w-12 text-sage animate-spin mx-auto mb-4" /><p className="text-charcoal-soft">Confirming your payment…</p></>}
        {state === "paid" && <>
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-charcoal">Payment received</h1>
          <p className="text-charcoal-soft mt-2">Thank you. Your invoice is now marked as paid.</p>
        </>}
        {state === "pending" && <>
          <Loader2 className="h-12 w-12 text-sage animate-spin mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-charcoal">Almost there</h1>
          <p className="text-charcoal-soft mt-2">Your payment is processing. You can safely return to the portal.</p>
        </>}
        {state === "error" && <>
          <XCircle className="h-14 w-14 text-destructive mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-charcoal">Something went wrong</h1>
          <p className="text-charcoal-soft mt-2">We couldn't confirm this payment. Please contact Nicole.</p>
        </>}
        <Link to="/portal" data-testid="back-to-portal" className="mt-6 inline-block rounded-full bg-sage text-white px-7 py-3 font-medium hover:bg-sage-dark transition-colors">Back to Portal</Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-sand-deep flex items-center justify-center px-5" data-testid="payment-cancel">
      <div className="bg-white rounded-[2rem] soft-shadow-lg p-10 max-w-md w-full text-center">
        <XCircle className="h-14 w-14 text-charcoal/40 mx-auto mb-4" />
        <h1 className="font-serif text-3xl text-charcoal">Payment canceled</h1>
        <p className="text-charcoal-soft mt-2">No charge was made. You can pay anytime from your portal.</p>
        <Link to="/portal" className="mt-6 inline-block rounded-full bg-sage text-white px-7 py-3 font-medium hover:bg-sage-dark transition-colors">Back to Portal</Link>
      </div>
    </div>
  );
}
